import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import React, { useContext, useEffect, useState } from 'react';
import { IStripePaymentId } from '../../../../interfaces';
import { setPendingPrivatePayment, stripeCancellationPayment } from '../../../../stores/payment.store';
import { useNavigate } from 'react-router-dom';
import { MainContext } from '../../../../context/main.context';
import {
    PAYMENTS_STRIPE_CLOSE_WIDGET_ERROR,
    PAYMENTS_STRIPE_SET_PENDING_PRIVATE_ERROR,
} from '../../../../common/errorCodes';
import ManualController from '../../../../hoc/ManualController';
import Payments from '../../index';


export default function CheckoutFormStripe(props: IStripePaymentId): JSX.Element {
    const navigate = useNavigate();
    const { stripePaymentId, paymentId } = props;
    const { toggleLoader, modalContent, locale, project, source, jwt, setModalContent } = useContext(MainContext);
    const [ message, setMessage ] = useState('');
    const [ isLoading, setIsLoading ] = useState(false);
    const { manualRedirect } = ManualController();

    const stripe: Stripe | null = useStripe();
    const elements: StripeElements | null = useElements();

    useEffect((): void => {
        if (!stripe) {
            return;
        }

        const clientSecret: string | null = new URLSearchParams(window.location.search).get(
            "payment_intent_client_secret"
        );

        if (!clientSecret) {
            return;
        }

        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }): void => {
            switch (paymentIntent?.status) {
                case 'succeeded':
                    setMessage('Payment succeeded!');
                    break;
                case 'processing':
                    setMessage('Your payment is processing.');
                    break;
                case 'requires_payment_method':
                    setMessage('Your payment was not successful, please try again.');
                    break;
                default:
                    setMessage('Something went wrong.');
                    break;
            }
        });
    }, [ stripe ]);

    const _handleSubmit = async (event: any): Promise<void> => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsLoading(true);

        const { error  } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/payments/done`,
            },
            redirect: 'if_required',
        });

        if (error) {
            setMessage(error?.message || 'error');
            return
        }

        _setPendingPrivatePayment();
    };

    const _setPendingPrivatePayment = (): void => {
        toggleLoader(true);

        setPendingPrivatePayment(paymentId).then(res => {
            const result: boolean = res?.data.result;
            const payload: string = res?.data.payload;
            const token = res?.data.token;

            if (result) {
                setIsLoading(false);
                manualRedirect({ payload, token }, 'payment');
                return;
            }

            manualRedirect(res, 'payment', true, PAYMENTS_STRIPE_SET_PENDING_PRIVATE_ERROR);
        }, err => {
            const errData = err?.response?.data || err;
            manualRedirect(errData, 'payment', true, PAYMENTS_STRIPE_SET_PENDING_PRIVATE_ERROR);
        }).finally((): void => {
            toggleLoader(false);
        });
    }

    const _navigate = (path: string): void => {
        navigate({
            pathname: path,
            search: `?id=${paymentId}&source=${source}&locale=${locale}&project=${project}&jwt=${jwt}`,
        });
    }

    const _onClose = (): void => {
        toggleLoader(true);

        stripeCancellationPayment(stripePaymentId, paymentId).then(res => {
            const result = res?.data?.result;

            if (result) {
                if (modalContent) {
                    setModalContent({ component: <Payments/> });
                    return;
                }

                _navigate('/payments/process');
                return;
            }

            manualRedirect(res, 'payment', true, PAYMENTS_STRIPE_CLOSE_WIDGET_ERROR);
        }, err => {
            const errData = err?.response?.data || err;
            manualRedirect(errData, 'payment', true, PAYMENTS_STRIPE_CLOSE_WIDGET_ERROR);
        }).finally((): void => {
            toggleLoader(false);
        });
    }

    return (
        <div className="stripe">
            <form className="stripe-payment-form" onSubmit={_handleSubmit}>
                <i className="gg-close stripe-payment-form-close-icon" onClick={_onClose}/>
                <PaymentElement/>
                <button className="stripe-button" disabled={isLoading || !stripe || !elements}>
                    <span>
                      {isLoading ? <div className="spinner" id="spinner"></div> : "Pay now"}
                    </span>
                </button>
                {message && <div className="stripe-payment-message">{message}</div>}
            </form>
        </div>
    );
}